import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Zap } from "lucide-react";

import { useAuth } from "@/components/AuthProvider";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { reopenIdleLaborSession } from "@/lib/labor/laborApi";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { PRODUCTION_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  listSdTestDevices,
  SDTESTAPP_DEFAULT_PORT,
  SdTestAppHttpClient,
  type SdTestDemographics,
  type SdTestDevice,
} from "@/lib/sdtestapp/sdTestAppHttpClient";
import { resolveSdTestAppControlLevel } from "@/lib/sdtestapp/sdTestAppControlLevel";
import {
  SdTestAppProductionClient,
  type ProductionBatchStatus,
  type ProductionRunHolderStatus,
  type ProductionSlotsState,
  type ProductionWorkflowState,
} from "@/lib/sdtestapp/sdTestAppProductionClient";

import { assignBenchRackSlotWithSwap } from "./bench/benchRackSlotAssign";
import { BenchBatchQcIncidentModal } from "./bench/BenchBatchQcIncidentModal";
import { BenchConnectionStatusButton } from "./BenchConnectionStatusButton";
import {
  CAL_TEST_PRODUCTION_TEST_STATUS_QUERY_KEY,
  fetchProductionTestStatusByMcuUuids,
  type ProductionTestDeviceStatus,
} from "./calTestApi";
import { readPolledDeviceMcuUuid } from "./configurationDeviceFields";
import { ProductionRackPanel } from "./ProductionRackPanel";
import { ProductionRunHolderOverlay } from "./ProductionRunHolderOverlay";
import {
  ProductionLaborClockOutModal,
  type ProductionLaborClockOutReason,
} from "./ProductionLaborClockOutModal";
import {
  downloadProductionReviewText,
  formatProductionReviewResults,
  formatSecondsEstimate,
  hubProductionStepTitles,
  recordVisibleRackMcusForLaborSession,
  useProductionStepConfirmReady,
} from "./productionRunHelpers";
import {
  clearHubProductionWorkflowPersisted,
  readHubProductionWorkflowPersisted,
  restoredHubProductionStepIndex,
  writeHubProductionWorkflowPersisted,
} from "./productionRunWorkflowStorage";

const PAGE_HELP =
  "Full Hub Production wizard for SGS employees at the test rack. Drives SDTestApp loopback for coil setup, calibration, 500 A bench check, unattended batch processing, wrap-up, and review.";

function activeDevices(demographics: SdTestDemographics | null): SdTestDevice[] {
  return listSdTestDevices(demographics);
}

export function ProductionRunPage(): JSX.Element {
  const { session: authSession } = useAuth();
  const hubUserId = authSession?.user?.id ?? "";
  const hubDisplayName =
    String(authSession?.user?.user_metadata?.full_name ?? authSession?.user?.email ?? "").trim() || hubUserId;
  const writesAllowed = useHubModuleWrite("manufacturing");
  const [laborModalOpen, setLaborModalOpen] = useState(false);
  const [laborModalReason, setLaborModalReason] = useState<ProductionLaborClockOutReason>("forced_server");
  const [laborModalMessage, setLaborModalMessage] = useState("");
  const [contestPending, setContestPending] = useState(false);
  const endedSessionIdRef = useRef<number | null>(null);
  const recordedMcusRef = useRef<Set<string>>(new Set());
  const lastLaborSessionIdRef = useRef<number | null>(null);
  const laborSessionIdRef = useRef<number | null>(null);
  const wrapUpModalShownRef = useRef(false);
  const workflowRestoreAttemptedRef = useRef(false);
  const laborClockInAdvanceRef = useRef(false);

  const openLaborModal = useCallback((reason: ProductionLaborClockOutReason, message: string, sessionId?: number | null) => {
    setLaborModalReason(reason);
    setLaborModalMessage(message);
    endedSessionIdRef.current = sessionId ?? laborSessionIdRef.current;
    setLaborModalOpen(true);
  }, []);

  const labor = useLaborSession({
    page: PRODUCTION_LABOR_PAGE,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Cal+Test",
    autoStart: true,
    onIdleTimeout: () => {
      openLaborModal(
        "idle_timeout",
        "You were clocked out due to 10 minutes of inactivity on Cal+Test. Contest the idle clock-out or clock in again to continue labor-gated steps.",
        laborSessionIdRef.current,
      );
    },
    onSessionEndedRemotely: () => {
      openLaborModal(
        "forced_server",
        "Your Cal+Test labor session ended on the server. Clock in again before wrap-up review actions.",
      );
    },
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const laborSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Cal+Test",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, laborSnapshot);

  const [port, setPort] = useState(SDTESTAPP_DEFAULT_PORT);
  const [draftPort, setDraftPort] = useState(SDTESTAPP_DEFAULT_PORT);
  const [reloadPending, setReloadPending] = useState(false);
  const [controlLevel, setControlLevel] = useState<"local" | "remote" | "unknown">("unknown");
  const [localBenchId, setLocalBenchId] = useState<string | null>(null);
  const [controlMessage, setControlMessage] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<ProductionWorkflowState | null>(null);
  const [slotsState, setSlotsState] = useState<ProductionSlotsState | null>(null);
  const [demographics, setDemographics] = useState<SdTestDemographics | null>(null);
  const [unattendedPhase, setUnattendedPhase] = useState("");
  const [unattendedRunning, setUnattendedRunning] = useState(false);
  const [coilCalRunning, setCoilCalRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [reviewResults, setReviewResults] = useState<unknown[]>([]);
  const [reviewDownloadText, setReviewDownloadText] = useState("");
  const [coilCalStatusText, setCoilCalStatusText] = useState("");
  const [unattendedBatch, setUnattendedBatch] = useState<ProductionBatchStatus | null>(null);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const [qcIncidentDevice, setQcIncidentDevice] = useState<SdTestDevice | null>(null);
  const [runHolderStatus, setRunHolderStatus] = useState<ProductionRunHolderStatus | null>(null);

  const productionClient = useMemo(() => new SdTestAppProductionClient(port), [port]);
  const httpClientRef = useRef<SdTestAppHttpClient | null>(null);
  const canActuate = controlLevel === "local" && writesAllowed;
  const stepIndex = workflow?.stepIndex ?? 0;
  const stepTitles = useMemo(() => hubProductionStepTitles(workflow?.stepTitles), [workflow?.stepTitles]);
  const visibleWizardSteps = useMemo(
    () =>
      stepTitles
        .map((title, engineIndex) => ({ title, engineIndex }))
        .filter((step) => step.engineIndex > 0),
    [stepTitles],
  );
  const wizardStepCount = visibleWizardSteps.length;
  const wizardDisplayStep =
    stepIndex <= 0 ? null : visibleWizardSteps.findIndex((step) => step.engineIndex === stepIndex) + 1 || stepIndex;
  const step1ConfirmReady = useProductionStepConfirmReady(stepIndex, 1);
  const step2ConfirmReady = useProductionStepConfirmReady(stepIndex, 2);
  const runHolderBlocked = Boolean(runHolderStatus?.blocked && runHolderStatus.blockingActive);

  const refreshRunHolderStatus = useCallback(async () => {
    if (controlLevel !== "local" || !hubUserId) return;
    try {
      const status = await productionClient.fetchRunHolderStatus(hubUserId);
      setRunHolderStatus(status);
    } catch {
      /* ignore polling errors */
    }
  }, [controlLevel, hubUserId, productionClient]);

  const ensureRunHolderClaim = useCallback(async () => {
    if (!hubUserId || !canActuate) return;
    if (runHolderStatus?.isHolder) return;
    const status = await productionClient.claimRunHolder(hubUserId, hubDisplayName);
    setRunHolderStatus(status);
    if (status.blocked) {
      throw new Error("Another operator is using Production on this bench.");
    }
  }, [canActuate, hubDisplayName, hubUserId, productionClient, runHolderStatus?.isHolder]);

  useEffect(() => {
    void refreshRunHolderStatus();
    if (controlLevel !== "local" || !hubUserId) return undefined;
    const intervalId = setInterval(() => void refreshRunHolderStatus(), 3000);
    return () => clearInterval(intervalId);
  }, [controlLevel, hubUserId, refreshRunHolderStatus]);

  useEffect(() => {
    if (controlLevel !== "local" || !hubUserId || !canActuate) return;
    if ((workflow?.stepIndex ?? 0) < 1) return;
    if (runHolderStatus?.isHolder || runHolderStatus?.holderKey) return;
    void productionClient
      .claimRunHolder(hubUserId, hubDisplayName)
      .then(setRunHolderStatus)
      .catch(() => undefined);
  }, [
    canActuate,
    controlLevel,
    hubDisplayName,
    hubUserId,
    productionClient,
    runHolderStatus?.holderKey,
    runHolderStatus?.isHolder,
    workflow?.stepIndex,
  ]);

  const recordRackMcusForLabor = useCallback(
    async (sessionId: number) => {
      await recordVisibleRackMcusForLaborSession({
        sessionId,
        productionClient,
        recordedMcus: recordedMcusRef.current,
      });
    },
    [productionClient],
  );

  useEffect(() => {
    laborSessionIdRef.current = labor.session?.id ?? null;
  }, [labor.session?.id]);

  const refreshAll = useCallback(async () => {
    if (controlLevel !== "local") return;
    try {
      const config = await productionClient.fetchConfig();
      let activeWorkflow = config.workflow;
      if (!workflowRestoreAttemptedRef.current) {
        workflowRestoreAttemptedRef.current = true;
        const persisted = readHubProductionWorkflowPersisted(port);
        const restoredStep = restoredHubProductionStepIndex(config.workflow, persisted);
        if (restoredStep != null) {
          activeWorkflow = await productionClient.setWorkflowStep(restoredStep);
        }
      }
      setWorkflow(activeWorkflow);
      writeHubProductionWorkflowPersisted(port, activeWorkflow);
      // Keep rack assignments reactive: clear stale connectors and refresh pool from live ports.
      const slots =
        stepIndex >= 1 && stepIndex <= 5
          ? await productionClient.syncSlots()
          : await productionClient.fetchSlots();
      if (stepIndex === 6 || unattendedRunning) {
        const batchStatus = await productionClient.fetchBatchStatus();
        if (batchStatus.slots && slots.slots.length > 0) {
          slots.slots = slots.slots.map((slot) => ({
            ...slot,
            status: batchStatus.slots?.[slot.slotName]?.status ?? slot.status,
          }));
        }
      }
      setSlotsState(slots);
      if (stepIndex === 6 || unattendedRunning) {
        const unattended = await productionClient.fetchUnattendedStatus();
        setWorkflow(unattended);
        setUnattendedPhase(unattended.phase);
        setUnattendedRunning(unattended.running);
        setUnattendedBatch(unattended.batch ?? null);
        if (!unattended.running && stepIndex === 6 && !wrapUpModalShownRef.current) {
          wrapUpModalShownRef.current = true;
          openLaborModal(
            "wrap_up",
            "Batch testing finished. Clock in for wrap-up labor before publishing the review summary.",
          );
        }
        if (unattended.stepIndex >= 7) {
          setUnattendedRunning(false);
        }
      }
      if (stepIndex === 3 || coilCalRunning) {
        const coilStatus = await productionClient.fetchCoilCalibrationStatus();
        setCoilCalRunning(coilStatus.running);
        setCoilCalStatusText(coilStatus.text || "");
        if (!coilStatus.running && stepIndex === 3) {
          const next = await productionClient.setWorkflowStep(4);
          setWorkflow(next);
        }
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error));
    }
  }, [controlLevel, port, productionClient, stepIndex, unattendedRunning, coilCalRunning, openLaborModal]);

  useEffect(() => {
    if (controlLevel !== "local" || workflow == null) return;
    writeHubProductionWorkflowPersisted(port, workflow);
  }, [controlLevel, port, workflow]);

  useEffect(() => {
    const client = new SdTestAppHttpClient(port, { onDemographics: setDemographics });
    httpClientRef.current = client;
    client.startPolling(3000);
    return () => client.dispose();
  }, [port]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const resolution = await resolveSdTestAppControlLevel(null, port);
      if (!cancelled) {
        setControlLevel(resolution.controlLevel);
        setLocalBenchId(resolution.loopbackMachineId);
        setControlMessage(resolution.message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [port]);

  useEffect(() => {
    if (controlLevel !== "local") return undefined;
    void refreshAll();
    const intervalId = setInterval(() => void refreshAll(), 2500);
    return () => clearInterval(intervalId);
  }, [controlLevel, refreshAll]);

  useEffect(() => {
    if (controlLevel !== "local") {
      setSelectiveRetestAvailable(undefined);
      return;
    }
    let cancelled = false;
    void productionClient.fetchSelectiveRetestCapability().then((capability) => {
      if (!cancelled) {
        setSelectiveRetestAvailable(capability.supported);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [controlLevel, productionClient]);

  useEffect(() => {
    if (controlLevel !== "remote") return;
    setLaborModalOpen(false);
    if (labor.isWorking) {
      void labor.stopWorking().catch(() => undefined);
    }
  }, [controlLevel, labor.isWorking, labor.stopWorking]);

  const devices = activeDevices(demographics);
  const rackMcuUuids = useMemo(
    () =>
      [...new Set(devices.map((device) => readPolledDeviceMcuUuid(device).trim().toUpperCase()).filter(Boolean))],
    [devices],
  );
  const productionTestStatusQuery = useQuery({
    queryKey: [CAL_TEST_PRODUCTION_TEST_STATUS_QUERY_KEY, rackMcuUuids.join(",")],
    queryFn: () => fetchProductionTestStatusByMcuUuids(rackMcuUuids),
    enabled: rackMcuUuids.length > 0,
    refetchInterval: 5000,
  });
  const productionTestStatusByMcuUuid = useMemo(() => {
    const map = new Map<string, ProductionTestDeviceStatus | null>();
    for (const entry of productionTestStatusQuery.data?.devices ?? []) {
      const key = String(entry.mcuUuid ?? "").trim().toUpperCase();
      if (key) {
        map.set(key, entry.status);
      }
    }
    return map;
  }, [productionTestStatusQuery.data]);
  const [selectiveRetestAvailable, setSelectiveRetestAvailable] = useState<boolean | undefined>(undefined);
  const deviceConnectorFingerprint = useMemo(
    () =>
      devices
        .map((device) => String(device.connectorId ?? device.cableSerialNumber ?? "").trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [devices],
  );
  const assignedCount = slotsState?.slots.filter((slot) => slot.connectorId).length ?? 0;

  useEffect(() => {
    if (controlLevel !== "local" || !canActuate) return;
    if (stepIndex < 1 || stepIndex > 5) return;
    let cancelled = false;
    void (async () => {
      try {
        await httpClientRef.current?.refresh();
        const slots = await productionClient.syncSlots();
        if (!cancelled) {
          setSlotsState(slots);
        }
      } catch {
        /* demographics/slot sync races are non-fatal; the poll loop retries */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canActuate, controlLevel, deviceConnectorFingerprint, productionClient, stepIndex]);

  useEffect(() => {
    if (!selectedConnectorId && devices[0]) {
      setSelectedConnectorId(String(devices[0].connectorId ?? ""));
    }
  }, [devices, selectedConnectorId]);

  useEffect(() => {
    if (!labor.isWorking || labor.session?.id == null || labor.session.id <= 0) return undefined;
    const sessionId = labor.session.id;
    if (lastLaborSessionIdRef.current !== sessionId) {
      recordedMcusRef.current.clear();
      lastLaborSessionIdRef.current = sessionId;
    }
    void recordRackMcusForLabor(sessionId).catch(() => undefined);
    const intervalId = setInterval(() => {
      void recordRackMcusForLabor(sessionId).catch(() => undefined);
    }, 15_000);
    return () => clearInterval(intervalId);
  }, [labor.isWorking, labor.session?.id, recordRackMcusForLabor]);

  const runStepAction = useCallback(
    async (action: () => Promise<void>) => {
      setActionError(null);
      setActionPending(true);
      try {
        if ((workflow?.stepIndex ?? 0) >= 0 && hubUserId) {
          await ensureRunHolderClaim();
        }
        await action();
        await refreshAll();
        await refreshRunHolderStatus();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : String(error));
      } finally {
        setActionPending(false);
      }
    },
    [ensureRunHolderClaim, hubUserId, refreshAll, refreshRunHolderStatus, workflow?.stepIndex],
  );

  useEffect(() => {
    setDraftPort(port);
  }, [port]);

  const reconnectBench = useCallback(async () => {
    setReloadPending(true);
    setActionError(null);
    try {
      setLoading(true);
      const resolution = await resolveSdTestAppControlLevel(null, port);
      setControlLevel(resolution.controlLevel);
      setLocalBenchId(resolution.loopbackMachineId);
      setControlMessage(resolution.message);
      await httpClientRef.current?.refresh();
      if (resolution.controlLevel === "local") {
        workflowRestoreAttemptedRef.current = false;
        await refreshAll();
        await refreshRunHolderStatus();
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
      setReloadPending(false);
    }
  }, [port, refreshAll, refreshRunHolderStatus]);

  useEffect(() => {
    if (!labor.isWorking && stepIndex === 0) {
      laborClockInAdvanceRef.current = false;
    }
  }, [labor.isWorking, stepIndex]);

  useEffect(() => {
    if (laborClockInAdvanceRef.current) return;
    if (!canActuate || !labor.isWorking || stepIndex !== 0 || loading || workflow === null) return;
    laborClockInAdvanceRef.current = true;
    void runStepAction(async () => {
      await productionClient.startScannerFastScan();
      const next = await productionClient.setWorkflowStep(1);
      setWorkflow(next);
    });
  }, [canActuate, labor.isWorking, loading, productionClient, runStepAction, stepIndex, workflow]);

  const calibrationEstimate = formatSecondsEstimate(workflow?.calibrationEstimatedSeconds);
  const runAllTestsEstimate = formatSecondsEstimate(workflow?.runAllTestsEstimatedSeconds);
  const canAssignRackSlots = stepIndex >= 1;

  const handleRackAssignSlot = useCallback(
    (slotName: string, connectorId: string | null, sourceSlotName?: string | null) => {
      void runStepAction(async () => {
        if (!connectorId) {
          const next = await productionClient.clearSlot(slotName);
          setSlotsState(next);
          return;
        }
        const next = await assignBenchRackSlotWithSwap(
          productionClient,
          slotsState?.slots ?? [],
          slotName,
          connectorId,
          sourceSlotName,
        );
        setSlotsState(next);
      });
    },
    [productionClient, runStepAction, slotsState?.slots],
  );

  const handleSetReferenceDevice = useCallback(
    (connectorId: string) => {
      void runStepAction(async () => {
        await productionClient.setReferenceDevice(connectorId);
        const slots = await productionClient.fetchSlots();
        setSlotsState(slots);
      });
    },
    [productionClient, runStepAction],
  );

  const stepPanel = (() => {
    if (!canActuate || workflow === null) {
      return null;
    }

    switch (stepIndex) {
      case 0:
        // Hub auto clock-in advances past this engine step; do not show it as a wizard step.
        return (
          <p className="text-sm text-muted-foreground">
            {labor.isWorking ? "Opening the load-coil step…" : "Starting Hub labor…"}
          </p>
        );
      case 1:
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Load the coil and cables, then continue to device confirmation.</p>
            <Button
              type="button"
              disabled={actionPending || !step1ConfirmReady}
              onClick={() => void runStepAction(async () => {
                const next = await productionClient.setWorkflowStep(2);
                setWorkflow(next);
              })}
            >
              Continue to device confirmation
            </Button>
          </div>
        );
      case 2:
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Connections rescan automatically while you are on this step. Use the coil panel to assign slots and set
              the reference device, then confirm below.
            </p>
            <Button
              type="button"
              disabled={actionPending || !slotsState?.hasReferenceDevice || assignedCount === 0 || !step2ConfirmReady}
              onClick={() => void runStepAction(async () => {
                await productionClient.stopScannerFastScan();
                const next = await productionClient.setWorkflowStep(3);
                setWorkflow(next);
              })}
            >
              Devices confirmed — start coil calibration
            </Button>
          </div>
        );
      case 3:
        return (
          <div className="space-y-3">
            <Badge tone={coilCalRunning ? "warning" : "muted"}>
              {coilCalRunning ? coilCalStatusText || "Coil calibration running…" : coilCalStatusText || "Coil calibration idle"}
            </Badge>
            {calibrationEstimate ? <p className="text-sm text-muted-foreground">{calibrationEstimate}</p> : null}
            <Button
              type="button"
              disabled={actionPending || coilCalRunning || !slotsState?.hasReferenceDevice}
              onClick={() => void runStepAction(async () => {
                await productionClient.startCoilCalibration();
                setCoilCalRunning(true);
              })}
            >
              Start coil calibration
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={actionPending || coilCalRunning}
              onClick={() => void runStepAction(async () => {
                const next = await productionClient.setWorkflowStep(4);
                setWorkflow(next);
              })}
            >
              Skip to 500 A bench check
            </Button>
          </div>
        );
      case 4:
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirm the bench is ready, run the 500 A replay, verify the ammeter, then continue.
            </p>
            <Badge tone={workflow.ready500aConfirmed ? "success" : "muted"}>
              {workflow.ready500aConfirmed ? "Ready confirmed" : "Confirm ready first"}
            </Badge>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={actionPending || workflow.ready500aConfirmed}
                onClick={() => void runStepAction(async () => {
                  const next = await productionClient.confirm500aReady();
                  setWorkflow(next);
                })}
              >
                Confirm ready
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={actionPending || !workflow.ready500aConfirmed}
                onClick={() => void runStepAction(async () => {
                  const result = await productionClient.powerPulse(500, 3);
                  if (!result.success) throw new Error(result.error ?? "Power pulse failed");
                })}
              >
                <Zap className="mr-2 h-4 w-4" />
                Replay 500 A (3s)
              </Button>
              <Button
                type="button"
                disabled={actionPending || !workflow.ready500aConfirmed}
                onClick={() => void runStepAction(async () => {
                  const next = await productionClient.setWorkflowStep(5);
                  setWorkflow(next);
                })}
              >
                Ammeter in range — next
              </Button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Clock out of labor before unattended processing (same as NiceGUI step 5).
            </p>
            <Button
              type="button"
              disabled={actionPending || !labor.isWorking}
              onClick={() => void runStepAction(async () => {
                const sessionId = labor.session?.id;
                if (sessionId && sessionId > 0) {
                  await recordRackMcusForLabor(sessionId);
                }
                await labor.stopWorking();
                const next = await productionClient.setWorkflowStep(6);
                setWorkflow(next);
                await productionClient.startUnattended();
                setUnattendedRunning(true);
              })}
            >
              Confirm ready — begin unattended processing
            </Button>
          </div>
        );
      case 6:
        return (
          <div className="space-y-3">
            <Badge tone={unattendedRunning ? "warning" : "success"}>
              {unattendedRunning ? `Running: ${unattendedPhase || "starting"}` : "Complete or idle"}
            </Badge>
            {runAllTestsEstimate ? <p className="text-sm text-muted-foreground">{runAllTestsEstimate}</p> : null}
            {unattendedBatch?.slots ? (
              <div className="max-h-48 overflow-y-auto rounded border p-2 text-xs">
                {Object.entries(unattendedBatch.slots).map(([slotName, slotStatus]) => (
                  <div key={slotName} className="flex justify-between gap-2 border-b py-1 last:border-b-0">
                    <span className="font-medium">{slotName}</span>
                    <span>{slotStatus.status}{slotStatus.error ? ` — ${slotStatus.error}` : ""}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {unattendedRunning ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={actionPending}
                  onClick={() => void runStepAction(async () => {
                    await productionClient.stopBatch();
                  })}
                >
                  Emergency stop batch
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={actionPending}
                  onClick={() => void runStepAction(async () => {
                    const next = await productionClient.cancelUnattended();
                    setWorkflow(next);
                    setUnattendedRunning(false);
                  })}
                >
                  Cancel unattended run
                </Button>
              </>
            ) : (
              <Button
                type="button"
                disabled={actionPending}
                onClick={() => void runStepAction(async () => {
                  const next = await productionClient.setWorkflowStep(7);
                  setWorkflow(next);
                })}
              >
                Continue to wrap-up
              </Button>
            )}
          </div>
        );
      case 7:
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Clock in for wrap-up labor before reviewing batch results.</p>
            <Button
              type="button"
              disabled={actionPending || labor.isWorking}
              onClick={() => void runStepAction(async () => {
                await labor.beginWorking();
                const next = await productionClient.setWorkflowStep(8);
                setWorkflow(next);
              })}
            >
              Clock in for wrap-up
            </Button>
          </div>
        );
      case 8:
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={actionPending}
                onClick={() => void runStepAction(async () => {
                  const result = await productionClient.publishReview();
                  setReviewResults(result.verificationResults);
                  setReviewDownloadText(result.downloadText ?? "");
                  const next = await productionClient.setWorkflowStep(9);
                  setWorkflow(next);
                })}
              >
                Publish batch review summary
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={actionPending}
                onClick={() => void runStepAction(async () => {
                  const summary = reviewDownloadText
                    ? { downloadText: reviewDownloadText, verificationResults: reviewResults }
                    : await productionClient.fetchReviewSummary();
                  setReviewDownloadText(summary.downloadText ?? "");
                  if (summary.verificationResults?.length) {
                    setReviewResults(summary.verificationResults);
                  }
                  if (summary.downloadText) {
                    downloadProductionReviewText(summary.downloadText);
                  }
                })}
              >
                Download review summary
              </Button>
            </div>
            {reviewResults.length > 0 ? (
              <pre className="max-h-48 overflow-auto rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap">
                {formatProductionReviewResults(reviewResults)}
              </pre>
            ) : null}
          </div>
        );
      case 9:
        return (
          <div className="space-y-3">
            {workflow.slowResetInfo ? (
              <p className="text-sm text-amber-800">{workflow.slowResetInfo}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Unrack devices for long-term test when ready.</p>
            )}
            <Button
              type="button"
              disabled={actionPending}
              onClick={() => void runStepAction(async () => {
                const next = await productionClient.setWorkflowStep(10);
                setWorkflow(next);
              })}
            >
              Unrack complete
            </Button>
          </div>
        );
      case 10:
        return (
          <div className="space-y-3">
            <Button
              type="button"
              disabled={actionPending}
              onClick={() => void runStepAction(async () => {
                if (labor.isWorking) {
                  await labor.stopWorking();
                }
                if (hubUserId) {
                  await productionClient.releaseRunHolder(hubUserId).catch(() => undefined);
                }
                const next = await productionClient.resetWorkflow();
                setWorkflow(next);
                setReviewResults([]);
                setReviewDownloadText("");
                wrapUpModalShownRef.current = false;
                workflowRestoreAttemptedRef.current = false;
                laborClockInAdvanceRef.current = false;
                recordedMcusRef.current.clear();
                clearHubProductionWorkflowPersisted();
              })}
            >
              Final clock out and restart workflow
            </Button>
          </div>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Cal+Test</PageTitleWithHelp>
        <div className="flex items-center gap-2">
          <BenchConnectionStatusButton
            port={port}
            draftPort={draftPort}
            onDraftPortChange={setDraftPort}
            onApplyPort={() => setPort(draftPort.trim() || SDTESTAPP_DEFAULT_PORT)}
            controlLevel={controlLevel}
            loading={loading}
            localBenchId={localBenchId}
            controlMessage={controlMessage}
            reloadPending={reloadPending}
            onReload={() => void reconnectBench()}
          />
          <LaborStationOperatorPresence stationPage="cal+test" />
        </div>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]">
        <Card className="relative">
          <CardHeader>
            <h2 className="text-lg font-semibold">
              {wizardDisplayStep == null
                ? "Wizard"
                : `Wizard — step ${wizardDisplayStep} of ${wizardStepCount || "?"}`}
            </h2>
            <p className="text-sm font-medium">
              {stepIndex <= 0 ? "Starting…" : (stepTitles[stepIndex] ?? "Unknown step")}
            </p>
          </CardHeader>
          <CardBody className="space-y-3">
            <ol className="grid gap-1 text-xs text-muted-foreground">
              {visibleWizardSteps.map((step, displayIndex) => (
                <li
                  key={`${step.engineIndex}-${step.title}`}
                  className={step.engineIndex === stepIndex ? "font-semibold text-foreground" : ""}
                >
                  {displayIndex + 1}. {step.title}
                </li>
              ))}
            </ol>
            {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}
            {actionPending ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Working…
              </p>
            ) : null}
            {stepPanel}
            {!writesAllowed ? (
              <p className="text-sm text-muted-foreground">Read-only role — actions disabled.</p>
            ) : null}
          </CardBody>
          {runHolderBlocked ? (
            <ProductionRunHolderOverlay
              holderKey={runHolderStatus?.holderKey ?? null}
              holderDisplayName={runHolderStatus?.holderDisplayName ?? null}
              viewerUserId={hubUserId}
              viewerDisplayName={hubDisplayName}
            />
          ) : null}
        </Card>

        <ProductionRackPanel
          slotsState={slotsState}
          devices={devices}
          selectedConnectorId={selectedConnectorId}
          stepIndex={stepIndex}
          actionPending={actionPending}
          canAssignSlots={canAssignRackSlots && writesAllowed}
          showReferenceControls={stepIndex >= 2 && writesAllowed}
          productionTestStatusByMcuUuid={productionTestStatusByMcuUuid}
          selectiveRetestAvailable={selectiveRetestAvailable}
          onSelectConnector={setSelectedConnectorId}
          onAssignSlot={handleRackAssignSlot}
          onSetReferenceDevice={handleSetReferenceDevice}
          onLogQcIncident={(connectorId) => {
            const device = devices.find((row) => String(row.connectorId ?? "") === connectorId) ?? null;
            setQcIncidentDevice(device);
          }}
          onStartSelectiveRetest={async (connectorId, testKeys) => {
            await runStepAction(async () => {
              const result = await productionClient.startSelectiveRetest({ connectorId, testKeys });
              if (!result.success) {
                throw new Error(result.error ?? "Selective retest failed.");
              }
              await productionTestStatusQuery.refetch();
            });
          }}
        />
      </div>
      <ProductionLaborClockOutModal
        open={laborModalOpen}
        reason={laborModalReason}
        message={laborModalMessage}
        contestPending={contestPending}
        onContest={() => {
          const sessionId = endedSessionIdRef.current;
          if (!sessionId) {
            setLaborModalOpen(false);
            return;
          }
          setContestPending(true);
          void reopenIdleLaborSession({ sessionId, domain: "manufacturing" })
            .then(() => labor.resumeOpenSessionFromServer())
            .then((resumed) => {
              setLaborModalOpen(false);
              if (!resumed) {
                setActionError("Idle clock-out was contested on the server, but the local session could not be restored. Use Clock in again.");
              }
            })
            .catch((error) => {
              setActionError(error instanceof Error ? error.message : String(error));
            })
            .finally(() => setContestPending(false));
        }}
        onClockInFresh={() => {
          setLaborModalOpen(false);
          void labor.beginWorking();
        }}
        onDismiss={() => setLaborModalOpen(false)}
      />
      <BenchBatchQcIncidentModal
        open={qcIncidentDevice !== null}
        device={qcIncidentDevice}
        onClose={() => setQcIncidentDevice(null)}
        onSubmitted={() => undefined}
      />
    </div>
  );
}
